import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  IconButton,
  CircularProgress,
  Typography,
  Popover,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import SmartDisplayOutlinedIcon from '@mui/icons-material/SmartDisplayOutlined';
import FileDownloadOffOutlinedIcon from '@mui/icons-material/FileDownloadOffOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { apiVss } from '../../../api/axiosClient';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import { useNotifier } from '../../../components/Notifier';
import dayjs from 'dayjs';
import { useLanguage } from '../../../utils/i18n';
import { API_BASES, VSS_CONFIG } from '../../../config/constants';

const replaceIpWithDomain = (url) => {
  if (!url) return url;
  // Match any IP optionally with port and replace with the configured HOST
  return url.replace(/[0-9.]+(:\d+)?/g, (match) => {
    // Basic IP shape check
    if (match.startsWith('103.169') || match.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/)) {
      return VSS_CONFIG.HOST;
    }
    return match;
  });
};

export default function CurrentDownloadVideoDialog({ open, onClose, vehicle, token }) {
  const notify = useNotifier();
  const [loading, setLoading] = useState(false);
  const [dataList, setDataList] = useState([]);
  const { t } = useLanguage();

  // Polling states
  const [anchorElDownload, setAnchorElDownload] = useState(null);
  const [downloadTask, setDownloadTask] = useState(null);
  const pollingRef = useRef(null);

  // Video states
  const [viewVideoDialog, setViewVideoDialog] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');

  // Delete state
  const [deleteDialog, setDeleteDialog] = useState({ open: false, row: null, loading: false });

  const fetchList = useCallback(async () => {
    if (!token || !vehicle) return;
    setLoading(true);
    try {
      const imei = vehicle.integration_imei || vehicle.deviceID;
      const today = dayjs().format('YYYY-MM-DD');

      const payload = {
        deviceNo: String(imei),
        pageCount: 100,
        pageNum: 1,
        scheme: 'http',
        taskEndTime: `${today} 23:59:59`,
        taskStartTime: `${today} 00:00:00`,
        taskState: null,
        token: String(token),
        username: ''
      };

      const res = await apiVss.post('/webdownrecord/findPage.action', payload);
      let list = [];
      if (res.data?.list) {
        list = res.data.list;
      } else if (res.data?.data?.list) {
        list = res.data.data.list;
      } else if (res.data?.data?.dataList) {
        list = res.data.data.dataList;
      } else if (Array.isArray(res.data)) {
        list = res.data;
      } else if (Array.isArray(res.data?.data)) {
        list = res.data.data;
      }

      // console.log('CurrentDownloadVideoDialog fetched data:', res.data);

      const mapped = list.map((item, index) => {
        let finalUrl = item.downUrl || item.video;
        if (!finalUrl && item.resWebPath) {
          finalUrl = `${apiVss}/fileSrv${item.resWebPath.startsWith('/') ? '' : '/'}${item.resWebPath}`;
        } else if (!finalUrl && item.path) {
          finalUrl = `${apiVss}/fileSrv${item.path.startsWith('/') ? '' : '/'}${item.path}`;
        } else if (!finalUrl && item.taskFilewebpath) {
          finalUrl = `${apiVss}/fileSrv${item.taskFilewebpath.startsWith('/') ? '' : '/'}${item.taskFilewebpath}`;
        }
        finalUrl = replaceIpWithDomain(finalUrl);

        return {
          id: item.taskId || index,
          device_imei: item.deviceNo || '-',
          camera: item.channel != null ? `Channel ${item.channel}` : '-',
          start: item.resStartTime || '',
          stop: item.resEndTime || '',
          taskCreateTime: item.taskCreateTime || '-',
          taskState: item.taskState, // 0: queue, 1: downloading, 2: finished, -1: error
          video: finalUrl,
          rawItem: item
        };
      });

      const sorted = mapped.sort((a, b) => {
        if (a.taskCreateTime === '-' || b.taskCreateTime === '-') return 0;
        return dayjs(b.taskCreateTime).unix() - dayjs(a.taskCreateTime).unix();
      });

      setDataList(sorted);
    } catch (err) {
      console.error(err);
      notify({ message: 'Failed to fetch download history', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [token, vehicle, notify]);

  useEffect(() => {
    if (open) {
      fetchList();
    } else {
      setDataList([]);
    }
  }, [open, fetchList]);

  // Handle Download from Server
  const handleDownloadFile = (url) => {
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewVideo = (url) => {
    setCurrentVideoUrl(url);
    setViewVideoDialog(true);
  };

  // Re-download Logic
  const handleInitDownload = useCallback(
    async (event, row) => {
      event.stopPropagation();
      setAnchorElDownload(event.currentTarget);
      setDownloadTask({ status: 'running', progress: 0, taskId: null, fileData: row });
      try {
        const payloadInsert = {
          username: 'admin',
          deviceNo: row.device_imei,
          channel: String(row.rawItem?.channel || 1).replace(/\D/g, ''),
          resStartTime: row.start,
          resEndTime: row.stop,
          resDeviceFileName: '',
          fileFormat: 'mp4',
          scheme: 'https',
          token: String(token),
          lang: 'en_US'
        };

        const res = await apiVss.post('/webdownrecord/insert.action', payloadInsert, {
          headers: { 'Content-Type': 'application/json' }
        });
        const data = res.data;

        if (data && data.status === 10000 && data.data?.taskId) {
          const taskId = data.data.taskId;
          const rawPath = data.data.resWebPath;
          try {
            const payloadTask = {
              taskId: taskId,
              token: String(token),
              action: 1,
              username: 'admin',
              resWebPath: rawPath
            };

            await apiVss.post('/webdownrecord/taskManagement.action', payloadTask, {
              headers: { 'Content-Type': 'application/json' }
            });

            setDownloadTask((prev) => ({ ...prev, taskId, progress: 5 }));

            // Start Polling
            if (pollingRef.current) clearInterval(pollingRef.current);
            pollingRef.current = setInterval(async () => {
              try {
                const pList = {
                  deviceNo: row.device_imei,
                  pageCount: 1,
                  pageNum: 1,
                  scheme: 'https',
                  taskEndTime: '',
                  taskStartTime: '',
                  taskState: null,
                  token: String(token),
                  username: 'admin'
                };
                const pollRes = await apiVss.post('/webdownrecord/findPage.action', pList, {
                  headers: { 'Content-Type': 'application/json' }
                });
                const pollData = pollRes.data?.data?.dataList || [];
                const foundTask = pollData.find((t) => String(t.taskId) === String(taskId));

                if (foundTask) {
                  let finalUrl = foundTask.video;
                  if (foundTask.path) {
                    finalUrl = `${API_BASES.VSS.replace('/vss', '')}/fileSrv${foundTask.path.startsWith('/') ? '' : '/'}${foundTask.path}`;
                  } else if (foundTask.taskFilewebpath) {
                    finalUrl = `${API_BASES.VSS.replace('/vss', '')}/fileSrv${foundTask.taskFilewebpath.startsWith('/') ? '' : '/'}${foundTask.taskFilewebpath}`;
                  }
                  finalUrl = replaceIpWithDomain(finalUrl);

                  if (foundTask.taskState === 2) {
                    clearInterval(pollingRef.current);
                    setDownloadTask((prev) => ({
                      ...prev,
                      status: 'success',
                      progress: 100,
                      resultUrl: finalUrl
                    }));
                    fetchList();
                  } else if (foundTask.taskState === -1) {
                    clearInterval(pollingRef.current);
                    setDownloadTask((prev) => ({
                      ...prev,
                      status: 'error',
                      errorMsg: 'Download failed or device offline.'
                    }));
                  } else {
                    const pct = parseFloat(foundTask.taskProgress || 0);
                    setDownloadTask((prev) => ({ ...prev, progress: pct > 5 ? pct : 5 }));
                  }
                }
              } catch (e) {
                console.error(e);
              }
            }, 2000);
          } catch (err) {
            setDownloadTask((prev) => ({ ...prev, status: 'error', errorMsg: err.message }));
          }
        } else {
          setDownloadTask((prev) => ({
            ...prev,
            status: 'error',
            errorMsg: data?.message || 'Failed to initialize download'
          }));
        }
      } catch (err) {
        setDownloadTask((prev) => ({ ...prev, status: 'error', errorMsg: err.message }));
      }
    },
    [token, fetchList]
  );

  const handleCloseDownload = () => {
    setAnchorElDownload(null);
    if (pollingRef.current) clearInterval(pollingRef.current);
    setDownloadTask(null);
  };

  const handleDeleteTask = async () => {
    const row = deleteDialog.row;
    if (!row) return;

    setDeleteDialog((prev) => ({ ...prev, loading: true }));
    try {
      const raw = row.rawItem || {};
      let actualTaskId = raw.guid || raw.taskId || row.id;
      if (String(actualTaskId) === '0' && raw.guid) {
        actualTaskId = raw.guid;
      }

      const payload = {
        taskId: String(actualTaskId),
        action: -1,
        resWebPath: raw.resWebPath || raw.taskFilewebpath || raw.downUrl || '',
        username: raw.username || 'admin',
        token: String(token)
      };

      const res = await apiVss.post('/webdownrecord/taskManagement.action', payload, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (res.data && res.data.status === 10000) {
        notify({ message: t('liveStreaming.succesDownloadDelete', 'Download task successfully deleted'), severity: 'success' });
        fetchList(); // Refresh
      } else {
        notify({ message: res.data?.message || t('liveStreaming.failedDelete', 'Failed to delete download task'), severity: 'error' });
      }
    } catch {
      notify({ message: t('liveStreaming.errorCorrupt', 'An error occurred while deleting the task'), severity: 'error' });
    } finally {
      setDeleteDialog({ open: false, row: null, loading: false });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {t('liveStreaming.todaysDownload', 'Todays Download History')}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t(
              'liveStreaming.thisInformation',
              'This information is only for todays download history. If you want to see more, please go to the'
            )}{' '}
            <strong>{t('liveStreaming.dowonloadHistory', 'Download History Video Page')}</strong>
          </Typography>
        </Box>
        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('liveStreaming.camera', 'Camera')}</TableCell>
                <TableCell>{t('achievement.startTime', 'Start Time')}</TableCell>
                <TableCell>{t('achievement.endTime', 'End Time')}</TableCell>
                <TableCell>{t('liveStreaming.downloadDate', 'Download Date')}</TableCell>
                <TableCell>{t('masterCompany.status', 'Status')}</TableCell>
                <TableCell align="center">{('general.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : dataList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    {t('liveStreaming.noDownload', 'No download history for today')}
                  </TableCell>
                </TableRow>
              ) : (
                dataList.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.camera}</TableCell>
                    <TableCell>{row.start}</TableCell>
                    <TableCell>{row.stop}</TableCell>
                    <TableCell>
                      {row.taskCreateTime && row.taskCreateTime !== '-' ? dayjs(row.taskCreateTime).format('DD/MM/YY HH:mm:ss') : '-'}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const state = row.taskState;
                        if (state === 2)
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CheckCircleIcon color="success" fontSize="small" />
                              <Typography variant="caption" color="success.main">
                                {t('notify.succes', 'Success')}
                              </Typography>
                            </Box>
                          );
                        if (state === 1)
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="caption" color="primary">
                                {t('liveStreaming.downloadingprocess', 'Downloading...')}
                              </Typography>
                            </Box>
                          );
                        if (state === 0)
                          return (
                            <Typography variant="caption" color="text.secondary">
                              {('liveStreaming.queued', 'Queued')}
                            </Typography>
                          );
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ErrorOutlineIcon color="error" fontSize="small" />
                            <Typography variant="caption" color="error">
                              {('liveStreaming.failed', 'Failed')}
                            </Typography>
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        {row.taskState === 2 && row.video ? (
                          <>
                            <IconButton size="small" onClick={() => handleViewVideo(row.video)} title="View Video" color="info">
                              <SmartDisplayOutlinedIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDownloadFile(row.video)} title="Download Video" color="primary">
                              <SaveAltIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            {row.taskState === -1 && (
                              <IconButton
                                size="small"
                                title="Unfinished"
                                color="default"
                                onClick={() => notify({ message: 'The video file is corrupted or incomplete.', severity: 'error' })}
                              >
                                <FileDownloadOffOutlinedIcon fontSize="small" />
                              </IconButton>
                            )}
                            <IconButton size="small" color="default" title="Redownload Video" onClick={(e) => handleInitDownload(e, row)}>
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          title="Delete Task"
                          onClick={() => setDeleteDialog({ open: true, row, loading: false })}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* DOWNLOAD POPOVER */}
        <Popover
          open={Boolean(anchorElDownload)}
          anchorEl={anchorElDownload}
          onClose={handleCloseDownload}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Box sx={{ p: 2, minWidth: 250, maxWidth: 300 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                {downloadTask?.status === 'running'
                  ? t('liveStreaming.downloadingVideo', 'Downloading Video...')
                  : downloadTask?.status === 'success'
                    ? t('liveStreaming.downloadComplete', 'Download Complete')
                    : t('liveStreaming.downloadFailed', 'Download Failed/Stopped on Server')}
              </Typography>
              <IconButton size="small" onClick={handleCloseDownload} sx={{ p: 0.5 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            {downloadTask?.status === 'running' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                <LinearProgress variant="determinate" value={downloadTask.progress} sx={{ flexGrow: 1 }} />
                <Typography variant="caption">{Math.round(downloadTask.progress)}%</Typography>
              </Box>
            )}
            {downloadTask?.status === 'success' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                <Typography variant="body2" color="success.main">
                  {t('liveStreaming.videoIsReady', 'Video is ready.')}
                </Typography>
                <Button variant="contained" size="small" onClick={() => handleDownloadFile(downloadTask.resultUrl)}>
                  {t('liveStreaming.downloadToPc', 'Download to PC')}
                </Button>
              </Box>
            )}
            {downloadTask?.status === 'error' && (
              <Typography variant="body2" color="error">
                {downloadTask.errorMsg}
              </Typography>
            )}
          </Box>
        </Popover>

        {/* VIEW VIDEO DIALOG */}
        <Dialog open={viewVideoDialog} onClose={() => setViewVideoDialog(false)} maxWidth="lg" fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {('liveStreaming.videoPreview', 'Video Preview')}
            <IconButton onClick={() => setViewVideoDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0, bgcolor: '#000', height: '70vh' }}>
            {currentVideoUrl ? (
              <video src={currentVideoUrl} controls autoPlay style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : (
              <Typography color="#fff" sx={{ p: 4 }}>
                {t('liveStreaming.noVideoSource', 'No Video Source')}
              </Typography>
            )}
          </DialogContent>
        </Dialog>

        {/* DELETE CONFIRM DIALOG */}
        <DeleteConfirmDialog
          open={deleteDialog.open}
          loading={deleteDialog.loading}
          onClose={() => setDeleteDialog({ open: false, row: null, loading: false })}
          onConfirm={handleDeleteTask}
          title={t('liveStreaming.deleteDownloadTask', 'Delete Download Task')}
          message={t('liveStreaming.areYouSure', 'Are you sure you want to delete this video download task and its file?')}
          itemName={deleteDialog.row?.camera ? `${deleteDialog.row.camera} (${deleteDialog.row.start} - ${deleteDialog.row.stop})` : ''}
        />
      </DialogContent>
    </Dialog>
  );
}
